import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Alert,
  Row,
  Col,
  Badge,
  Spinner,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import {
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Phone,
  Mail,
  Calendar,
  Shield,
  Lock,
  ArrowRight,
  Info,
} from "react-feather";
import paymentLinkService from "../../../services/PaymentLinkService";
import paymentGatewayService from "../../../services/PaymentGatewayService";
import { toast } from "react-toastify";
import { updatePaymentStatusToBackend } from "../../../utils/paymentUtils";
import logo from "../../../assets/images/logo.png";

const PaymentLinkPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();

  const [paymentLink, setPaymentLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Loading, 2: Payment Details, 3: Processing, 4: Success
  function formatToDDMMYYYY(dateStr) {
    if (!dateStr) return "N/A";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
    loadPaymentLink();
  }, [linkId]);

  const loadPaymentLink = async () => {
    setLoading(true);
    setError(null);
    setCurrentStep(1);

    try {
      // Extract payment data from the encoded linkId
      let paymentData = null;
      let patientId = null;
      let appointmentId = null;
      let leadId = null;
      let prescriptionId = null;
      let queueId = null;
      let paymentFor = null;
      let paymentType = null;
      let patientName = null;
      let email = null;
      let decodedAmount = null;
      try {
        // Decode the base64 encoded payment data
        const decodedData = atob(linkId);
        paymentData = JSON.parse(decodedData);

        console.log("🔍 PaymentLinkPage - Decoded Data:", paymentData);

        patientId = paymentData.patientId;
        appointmentId = paymentData.appointmentId;
        leadId = paymentData.leadId;
        prescriptionId = paymentData.prescriptionId;
        queueId = paymentData.queueId;
        paymentFor = paymentData.paymentFor;
        paymentType = paymentData.paymentType;
        patientName = paymentData.patientName;
        // patientCode = paymentData.patientCode;
        email = paymentData.email;
        // Extract the calculated amount from the module
        decodedAmount = paymentData.amount || 0;

        console.log("🔍 PaymentLinkPage - Extracted IDs:", {
          patientId,
          appointmentId,
          leadId,
          prescriptionId,
          queueId,
          paymentFor,
          paymentType,
          patientName,
          email,
          decodedAmount,
        });
      } catch (decodeError) {
        console.error("Error decoding payment link data:", decodeError);
        // Fallback: treat linkId as patientId for backward compatibility
        patientId = linkId;
      }

      console.log("🔍 PaymentLinkPage - Extracted Data:", {
        patientId,
        appointmentId,
        leadId,
        prescriptionId,
        queueId,
        paymentFor,
        decodedAmount

      });

      // Call backend API to get payment link data with extracted IDs
      const result = await paymentLinkService.getPaymentLinkData(

        patientId,
        {
          appointmentId,
          leadId,
          prescriptionId,
          queueId,
          paymentFor,
          decodedAmount
        }
      );

      console.log("🔍 PaymentLinkPage - API Result:", result);

      if (result.success) {
        const paymentData = result.data;

        // Check if link is expired (if expiresAt is provided in the response)
        if (
          paymentData.expiresAt &&
          new Date() > new Date(paymentData.expiresAt)
        ) {
          setError(
            "This payment link has expired. Please contact the clinic for a new payment link."
          );
          setLoading(false);
          return;
        }

        // Use the calculated amount passed from the module via encoded URL data
        // This amount comes from the encoded payment data and represents the exact calculated amount
        // from each specific module (counsellor, appointment, etc.)
        const paymentAmount = decodedAmount || 0;

        console.log("🔍 PaymentLinkPage - Payment Amount:", {
          paymentFor,
          paymentAmount,
          decodedAmount,
          backendData: paymentData
        });

        const formattedPaymentLink = {
          id: linkId,
          status: "PENDING", // Default status since it's not in the response
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
          metadata: {
            patientName:
              patientName ||
              `${paymentData.first_name || ""} ${paymentData.middile_name || ""
                } ${paymentData.last_name || ""}`.trim() || "Patient",
            patientPhone: paymentData.phone_no || paymentData.additional_contact_no || "N/A",
            patientEmail:
              paymentData.other_email || paymentData.patient_email || paymentData.email || null,
            patientCode: paymentData.patient_code || "N/A",
            patientPhoto: paymentData.lead_photo || null,
            gender: paymentData.gender || "N/A",
            dateOfBirth: formatToDDMMYYYY(paymentData.date_of_birth) || "N/A",
            height: paymentData.height || "N/A",
            weight: paymentData.weight || "N/A",
            occupation: paymentData.occupation || "N/A",
            idProof: paymentData.id_proof || "N/A",
            idNumber: paymentData.id_number || "N/A",
            patientStatus: paymentData.lead_status || "N/A",
            branch: paymentData.branch_id || "N/A",
            amount: paymentAmount,
            description: `Payment for ${paymentFor}`,
            paymentType: paymentFor.toLowerCase(),
            //createdBy: "Staff",
            // Address information
            temporaryAddress: paymentData.TemporaryAddress || null,
            permanentAddress: paymentData.PermanentAddress || null,
            // Other information
            otherInfo: paymentData.OtherInfo || null,
            // Diseases
            diseases: paymentData.diseases || [],
            // Marriage info
            marriageInfo: paymentData.marriageInfo || [],
          },
          paymentData: {
            amount: paymentAmount,
            customerName:
              patientName ||
              `${paymentData.first_name || ""} ${paymentData.middile_name || ""
                } ${paymentData.last_name || ""}`.trim() || "Patient",
            customerEmail:
              email || paymentData.patient_email || paymentData.email || null,
            customerPhone:
              paymentData.whatsapp_contact_no ||
              paymentData.additional_contact_no ||
              "N/A",
            description: `${paymentFor}`,
            patientId: paymentData.patient_id,
            patientCode: paymentData.patient_code,
          },
        };

        setPaymentLink(formattedPaymentLink);
        setLoading(false);
        setCurrentStep(2);
      } else {
        setError(
          result.error ||
          "Payment link not found or has expired. Please check the link or contact the clinic for a new payment link."
        );
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading payment link:", error);
      setError(
        "Failed to load payment link. Please try again or contact support."
      );
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!paymentLink) return;

    setProcessingPayment(true);
    setCurrentStep(3);

    try {
      // Extract decoded IDs from the linkId
      let decodedPatientId = null;
      let decodedAppointmentId = null;
      let decodedLeadId = null;
      let decodedPrescriptionId = null;
      let decodedQueueId = null;
      let decodedPaymentFor = null;
      let decodedPaymentType = null;
      let decodedPatientName = null;
      let decodedEmail = null;
      let decodedAmount = null;

      try {
        const decodedData = atob(linkId);
        const paymentData = JSON.parse(decodedData);
        decodedPatientId = paymentData.patientId;
        decodedAppointmentId = paymentData.appointmentId;
        decodedLeadId = paymentData.leadId;
        decodedPrescriptionId = paymentData.prescriptionId;
        decodedQueueId = paymentData.queueId;
        decodedPaymentFor = paymentData.paymentFor;
        decodedPaymentType = paymentData.paymentType;
        decodedPatientName = paymentData.patientName;
        decodedEmail = paymentData.email;
        // Extract the calculated amount from the module
        decodedAmount = paymentData.amount || 0;
      } catch (decodeError) {
        console.error("Error decoding payment data for payment:", decodeError);
        // Fallback: use linkId as patientId
        decodedPatientId = linkId;
      }

      console.log("🔍 PaymentLinkPage - Payment Data with Decoded IDs:", {
        decodedPatientId,
        decodedAppointmentId,
        decodedLeadId,
        decodedPrescriptionId,
        decodedQueueId,
        decodedPaymentFor,
        decodedPaymentType,
        decodedPatientName,
        decodedEmail,
        decodedAmount,
      });

      console.log(
        "🔍 PaymentLinkPage - Payment Link Data:",
        paymentLink.paymentData
      );

      // Prepare payment data for ICICI in snake_case format as expected by API
      const paymentData = {
        // IDs in snake_case format
        patient_id: decodedPatientId,
        appointment_id: decodedAppointmentId,
        lead_id: decodedLeadId,
        prescription_id: decodedPrescriptionId,
        queue_id: decodedQueueId,
        payment_for: decodedPaymentFor,

        // Payment details in snake_case format
        // Use the amount passed from the module instead of from paymentLink
        amount: parseFloat(decodedAmount || paymentLink.paymentData.amount),
        customer_name: decodedPatientName || paymentLink.paymentData.customerName,
        customer_email: decodedEmail || paymentLink.paymentData.customerEmail,
        customer_mobile: paymentLink.paymentData.customerPhone,
        customer_address: paymentLink.paymentData.customerAddress || "",
        description: paymentLink.paymentData.description,
      };

      console.log(
        "🔍 PaymentLinkPage - Final Payment Data being sent:",
        paymentData
      );

      const response = await paymentGatewayService.initiatePaymentSale(
        paymentData
      );

      if (response.success) {
        if (response.data.redirectUrl) {
          // Real payment gateway - redirect to external page
          toast.success("Redirecting to secure payment gateway...");
          // Store payment data for return handler with ALL initiateSale parameters
          const paymentDataForStorage = {
            transactionNumber: response.data.transactionNumber,
            linkId: linkId,
            // All the same parameters that were sent to initiateSale
            patient_id: decodedPatientId,
            appointment_id: decodedAppointmentId,
            lead_id: decodedLeadId,
            prescription_id: decodedPrescriptionId,
            queue_id: decodedQueueId,
            payment_for: decodedPaymentFor,
            amount: paymentData.amount,
            customer_name: paymentData.customer_name,
            customer_email: paymentData.customer_email,
            customer_mobile: paymentData.customer_mobile,
            customer_address: paymentData.customer_address,
            description: paymentData.description,
            payment_type: decodedPaymentType,
            customerData: {
              name: paymentData.customer_name,
              email: paymentData.customer_email,
              phone: paymentData.customer_mobile,
            },
            paymentType: decodedPaymentType || paymentLink.metadata.paymentType,
          };

          localStorage.setItem("pendingPayment", JSON.stringify(paymentDataForStorage));
          localStorage.setItem("currentPayment", JSON.stringify(paymentDataForStorage));
          window.location.href = response.data.redirectUrl;
          setShowPaymentModal(false);
        } else {
          // Mock payment - simulate success directly
          toast.success("Payment initiated successfully!");
          setTimeout(async () => {
            await handlePaymentSuccess({
              transactionNumber: response.data.transactionNumber,
              status: "SUCCESS",
              responseCode: "000",
              responseMessage: "SUCCESS",
            });
          }, 2000);
        }
      } else {
        throw new Error(response.error || "Payment initiation failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.message || "Payment failed. Please try again.");
      setCurrentStep(2); // Go back to payment details
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      // Extract patient ID and other data from the decoded payment data
      let patientId = linkId;
      let paymentData = {};
      let decodedAmount = 0;
      let decodedPaymentType = "";
      let decodedPaymentFor = "";
      let decodedQueueId = "";
      let decodedAppointmentId = null;
      let decodedLeadId = null;
      let decodedPrescriptionId = null;

      try {
        const decodedData = atob(linkId);
        paymentData = JSON.parse(decodedData);
        patientId = paymentData.patientId;
        decodedAmount = paymentData.amount || 0;
        decodedPaymentType = paymentData.paymentType || "";
        decodedPaymentFor = paymentData.paymentFor || "";
        decodedQueueId = paymentData.queueId || "";
        decodedAppointmentId = paymentData.appointmentId;
        decodedLeadId = paymentData.leadId;
        decodedPrescriptionId = paymentData.prescriptionId;
      } catch {
        // Fallback: use linkId as patientId
        patientId = linkId;
      }

      // Forward ALL the same parameters that were sent to initiateSale
      const backendPaymentData = {
        token: localStorage.getItem("token"),
        admin_id: localStorage.getItem("admin_id"),
        patient_id: patientId,
        payment_status: "success",
        payment_response: JSON.stringify({
          patient_id: patientId,
          merchantTxnNo: paymentDetails.transactionNumber || "",
          responseCode: paymentDetails.responseCode || "000",
          responseDescription: paymentDetails.responseMessage || "SUCCESS",
          secureHash: paymentDetails.secureHash || "",
          txnDate:
            paymentDetails.txnDate ||
            new Date().toISOString().split("T")[0].replace(/-/g, ""),
          txnTime:
            paymentDetails.txnTime ||
            new Date()
              .toISOString()
              .split("T")[1]
              .split(".")[0]
              .replace(/:/g, ""),
          aggregatorID: paymentDetails.aggregatorID || "",
          bankTxnNo:
            paymentDetails.bankTxnNo || paymentDetails.transactionNumber || "",
          cardMask: paymentDetails.cardMask || "",
          paymentMode: paymentDetails.paymentMode || "UPI",
          addlParam1: paymentDetails.addlParam1 || "",
          addlParam2: paymentDetails.addlParam2 || "",
          customerEmailID:
            paymentDetails.customerEmailID || paymentData.customer_email || "",
          customerMobileNo:
            paymentDetails.customerMobileNo ||
            paymentData.customer_mobile ||
            "",
          merchantId: paymentDetails.merchantId || "",
          txnID: paymentDetails.txnID || paymentDetails.transactionNumber || "",
          status: paymentDetails.status || "success",
          error: paymentDetails.error || "",
          paymentDateTime:
            paymentDetails.paymentDateTime ||
            new Date().toISOString().replace(/[-:]/g, "").split(".")[0],
          fullUrl: window.location.href || "",
        }),
        transaction_id: paymentDetails.transactionNumber,
        // Forward ALL the same parameters from initiateSale
        appointment_id: decodedAppointmentId || paymentData.appointment_id || "",
        lead_id: decodedLeadId || paymentData.lead_id || "",
        prescription_id: decodedPrescriptionId || paymentData.prescription_id || "",
        queue_id: decodedQueueId || paymentData.queue_id || "",
        payment_for: decodedPaymentFor || paymentData.payment_for || "",
        amount: decodedAmount || paymentData.amount || 0,
        customer_name: decodedPatientName || paymentData.customer_name || paymentData.customerName || "",
        customer_email: decodedEmail || paymentData.customer_email || paymentData.customerEmail || "",
        customer_mobile: paymentData.customer_mobile || paymentData.customerPhone || "",
        customer_address: paymentData.customer_address || paymentData.customerAddress || "",
        description: paymentData.description || "",
        payment_type: decodedPaymentType || paymentData.payment_type || "",
        coupon_code: paymentData.coupon_code || "",
        final_amount: decodedAmount || paymentData.final_amount || paymentData.amount || 0,
        reference_id: decodedAppointmentId || decodedLeadId || decodedPrescriptionId || paymentData.reference_id || "",
      };

      // 🔍 DEBUG: Log all parameters being sent to updatePaymentStatus API
      console.log("🔍 DEBUG: PaymentLinkPage - updatePaymentStatus API Parameters");
      console.log("📍 Source: PaymentLinkPage.handlePaymentSuccess");
      console.log("📋 Raw backendPaymentData:", backendPaymentData);
      console.log("🔑 Token:", backendPaymentData.token ? "Present" : "Missing");
      console.log("👤 Admin ID:", backendPaymentData.admin_id);
      console.log("🏥 Patient ID:", backendPaymentData.patient_id);
      console.log("📅 Appointment ID:", backendPaymentData.appointment_id);
      console.log("🎯 Lead ID:", backendPaymentData.lead_id);
      console.log("💊 Prescription ID:", backendPaymentData.prescription_id);
      console.log("📋 Queue ID:", backendPaymentData.queue_id);
      console.log("💰 Amount:", backendPaymentData.amount);
      console.log("💳 Payment Type:", backendPaymentData.payment_type);
      console.log("🎯 Payment For:", backendPaymentData.payment_for);
      console.log("🎫 Transaction ID:", backendPaymentData.transaction_id);
      console.log("🏷️ Reference ID:", backendPaymentData.reference_id);
      console.log("🎫 Coupon Code:", backendPaymentData.coupon_code);
      console.log("💰 Final Amount:", backendPaymentData.final_amount);
      console.log("📊 Payment Status:", backendPaymentData.payment_status);

      // Log decoded data sources
      console.log("🔍 DEBUG: Decoded Data Sources");
      console.log("📅 Decoded Appointment ID:", decodedAppointmentId);
      console.log("🎯 Decoded Lead ID:", decodedLeadId);
      console.log("💊 Decoded Prescription ID:", decodedPrescriptionId);
      console.log("📋 Decoded Queue ID:", decodedQueueId);
      console.log("💰 Decoded Amount:", decodedAmount);
      console.log("💳 Decoded Payment Type:", decodedPaymentType);
      console.log("🎯 Decoded Payment For:", decodedPaymentFor);

      // 🔍 DEBUG: Parameter Mapping from initiateSale to updatePaymentStatus
      console.log("🔍 DEBUG: Parameter Mapping - initiateSale → updatePaymentStatus");
      console.log("📋 initiateSale parameters that are now forwarded:");
      console.log("  - patient_id:", backendPaymentData.patient_id);
      console.log("  - appointment_id:", backendPaymentData.appointment_id);
      console.log("  - lead_id:", backendPaymentData.lead_id);
      console.log("  - prescription_id:", backendPaymentData.prescription_id);
      console.log("  - queue_id:", backendPaymentData.queue_id);
      console.log("  - payment_for:", backendPaymentData.payment_for);
      console.log("  - amount:", backendPaymentData.amount);
      console.log("  - customer_name:", backendPaymentData.customer_name);
      console.log("  - customer_email:", backendPaymentData.customer_email);
      console.log("  - customer_mobile:", backendPaymentData.customer_mobile);
      console.log("  - customer_address:", backendPaymentData.customer_address);
      console.log("  - description:", backendPaymentData.description);

      // Update payment status via backend API
      const updateResult = await updatePaymentStatusToBackend(
        backendPaymentData
      );

      if (updateResult.success) {
        // Update local state
        setPaymentLink((prev) => ({
          ...prev,
          status: "PAID",
          paymentDetails: paymentDetails,
        }));

        toast.success("Payment completed successfully!");
        setCurrentStep(4);

        // Show success message
        setShowPaymentModal(false);

        // Redirect to success page after a delay with ALL initiateSale parameters
        setTimeout(() => {
          // Build success URL with ALL initiateSale parameters
          const successParams = new URLSearchParams();

          // Payment details
          successParams.set("transactionNumber", paymentDetails.transactionNumber || "");
          successParams.set("status", paymentDetails.status || "SUCCESS");
          successParams.set("responseCode", paymentDetails.responseCode || "000");
          successParams.set("responseMessage", paymentDetails.responseMessage || "SUCCESS");

          // ✅ ALL initiateSale parameters
          successParams.set("patient_id", decodedPatientId || "");
          successParams.set("appointment_id", decodedAppointmentId || "");
          successParams.set("lead_id", decodedLeadId || "");
          successParams.set("prescription_id", decodedPrescriptionId || "");
          successParams.set("queue_id", decodedQueueId || "");
          successParams.set("payment_for", decodedPaymentFor || "");
          successParams.set("payment_type", decodedPaymentType || "");
          successParams.set("amount", decodedAmount || "");
          successParams.set("customer_name", decodedPatientName || "");
          successParams.set("customer_email", decodedEmail || "");
          successParams.set("customer_mobile", paymentLink.paymentData.customerPhone || "");
          successParams.set("customer_address", paymentLink.paymentData.customerAddress || "");
          successParams.set("description", paymentLink.paymentData.description || "");
          successParams.set("reference_id", decodedAppointmentId || decodedLeadId || decodedPrescriptionId || "");

          const successUrl = `/payment/success?${successParams.toString()}`;
          console.log("🎯 PaymentLinkPage redirecting to success with ALL initiateSale parameters:", successUrl);

          navigate(successUrl, {
            state: {
              paymentDetails,
              paymentLink,
            },
          });
        }, 3000);
      } else {
        throw new Error(
          updateResult.error || "Failed to update payment status"
        );
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Payment completed but status update failed");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        variant: "warning",
        text: "Payment Pending",
        icon: <Clock size={16} />,
      },
      PAID: {
        variant: "success",
        text: "Payment Completed",
        icon: <CheckCircle size={16} />,
      },
      EXPIRED: {
        variant: "danger",
        text: "Link Expired",
        icon: <XCircle size={16} />,
      },
      CANCELLED: {
        variant: "secondary",
        text: "Cancelled",
        icon: <XCircle size={16} />,
      },
    };

    const config = statusConfig[status] || {
      variant: "secondary",
      text: status,
      icon: <AlertTriangle size={16} />,
    };
    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        {config.icon}
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };



  const getStepProgress = () => {
    switch (currentStep) {
      case 1:
        return 25;
      case 2:
        return 50;
      case 3:
        return 75;
      case 4:
        return 100;
      default:
        return 0;
    }
  };

  const getStepText = () => {
    switch (currentStep) {
      case 1:
        return "Loading payment details...";
      case 2:
        return "Review and complete payment";
      case 3:
        return "Processing payment...";
      case 4:
        return "Payment completed!";
      default:
        return "";
    }
  };

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ backgroundColor: "#F6FBF8" }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "12px" }}
              >
                <Card.Body className="text-center py-5">
                  <div className="mb-4">
                    <img
                      src={logo}
                      alt="Rohati Logo"
                      style={{
                        height: "60px",
                        width: "auto",
                        marginBottom: "20px",
                      }}
                    />
                  </div>
                  <Spinner
                    animation="border"
                    style={{ color: "#007680" }}
                    size="lg"
                    className="mb-4"
                  />
                  <h4
                    className="mb-3"
                    style={{ color: "#3d4465", fontWeight: "600" }}
                  >
                    Loading Your Payment
                  </h4>
                  <p className="text-muted mb-0">
                    Please wait while we load your payment details...
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ backgroundColor: "#F6FBF8" }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "12px" }}
              >
                <Card.Body className="text-center py-5">
                  <div className="mb-4">
                    <img
                      src={logo}
                      alt="Rohati Logo"
                      style={{
                        height: "60px",
                        width: "auto",
                        marginBottom: "20px",
                      }}
                    />
                  </div>
                  <XCircle
                    size={64}
                    style={{ color: "#F46B68" }}
                    className="mb-4"
                  />
                  <h4
                    style={{ color: "#F46B68", fontWeight: "600" }}
                    className="mb-3"
                  >
                    Payment Link Error
                  </h4>
                  <p className="text-muted mb-4">{error}</p>
                  {/* <div className="d-flex gap-2 justify-content-center">
                    <Button
                      variant="outline-primary"
                      onClick={() => window.close()}
                      style={{ borderColor: "#007680", color: "#007680" }}
                    >
                      Close Window
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => window.location.reload()}
                      style={{
                        backgroundColor: "#007680",
                        borderColor: "#007680",
                      }}
                    >
                      Try Again
                    </Button>
                  </div> */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          backgroundColor: "#F6FBF8",
          minHeight: "100vh",
          padding: "20px 0",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              {/* Header */}
              <div className="text-center mb-4">
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <div className="me-3">
                    <img
                      src={logo}
                      alt="Rohati Logo"
                      style={{
                        height: "120px",
                        width: "auto",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Card */}
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "12px" }}
              >
                <Card.Header
                  style={{
                    backgroundColor: "#007680",
                    color: "white",
                    padding: "1rem 1.5rem",
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 d-flex align-items-center">
                      <CreditCard size={20} className="me-2" />
                      Payment Details
                    </h5>
                    <div className="ms-3">{getStatusBadge(paymentLink.status)}</div>
                  </div>

                </Card.Header>

                <Card.Body className="p-4">
                  {/* What You're Paying For */}
                  <div className="mb-4">
                    <h6
                      style={{ color: "#007680", fontWeight: "600" }}
                      className="mb-3"
                    >
                      <Info size={16} className="me-2" />
                      Payment Details
                    </h6>
                    <div
                      style={{ backgroundColor: "#f8f9fa" }}
                      className="p-3 rounded"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">Service Type:</span>
                        <span className="text-capitalize">
                          {paymentLink.metadata.paymentType}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">Description:</span>
                        <span className="text-muted">
                          {paymentLink.metadata.description}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Amount:</span>
                        <span className="h5 mb-0" style={{ color: "#007680" }}>
                          ₹{Number(paymentLink.metadata.amount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Patient Information */}
                  <div className="mb-4">
                    <h6
                      style={{ color: "#007680", fontWeight: "600" }}
                      className="mb-3"
                    >
                      <User size={16} className="me-2" />
                      Patient Information
                    </h6>

                    {/* Patient Photo and Basic Info */}
                    <div className="d-flex align-items-start mb-3">
                      {paymentLink.metadata.patientPhoto && (
                        <img
                          src={paymentLink.metadata.patientPhoto}
                          alt="Patient"
                          className="rounded-circle me-3"
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      {/* Patient Information Box */}
                      <div
                        style={{ backgroundColor: "#f8f9fa" }}
                        className="p-3 rounded mb-3 flex-grow-1"
                      >
                        <Row>
                          <Col md={6}>
                            <div className="d-flex align-items-center mb-2">
                              <User size={16} className="me-2 text-muted" />
                              <span>
                                <strong>Patient Name:</strong>{" "}
                                <span className="text-capitalize">
                                  {paymentLink.metadata.patientName}
                                </span>
                              </span>
                            </div>
                          </Col>

                          {paymentLink?.metadata?.patientCode &&
                            paymentLink.metadata.patientCode !== "N/A" &&
                            paymentLink.metadata.patientCode.trim() !== "" && (
                              <Col md={6}>
                                <div className="d-flex align-items-center mb-2">
                                  <span>
                                    <strong>Patient Code:</strong>{" "}
                                    <span className="text-muted">
                                      {paymentLink.metadata.patientCode}
                                    </span>
                                  </span>
                                </div>
                              </Col>
                            )}
                        </Row>

                      </div>
                    </div>

                    {/* Contact Information */}
                    <div
                      style={{ backgroundColor: "#f8f9fa" }}
                      className="p-3 rounded mb-3"
                    >
                      <h6
                        style={{ color: "#007680", fontWeight: "600" }}
                        className="mb-2"
                      >
                        Contact Information
                      </h6>
                      <Row>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <Phone size={16} className="me-2 text-muted" />
                            <span>
                              <strong>WhatsApp:</strong>{" "}
                              {paymentLink.metadata.patientPhone}
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <Mail size={16} className="me-2 text-muted" />
                            <span>
                              <strong>Email:</strong>{" "}
                              {paymentLink.metadata.patientEmail}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      {paymentLink.metadata.additional_contact_no && (
                        <div className="d-flex align-items-center mb-2">
                          <Phone size={16} className="me-2 text-muted" />
                          <span>
                            <strong>Additional Contact:</strong>{" "}
                            {paymentLink.metadata.additional_contact_no}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Personal Information */}
                    <div
                      style={{ backgroundColor: "#f8f9fa" }}
                      className="p-3 rounded mb-3"
                    >
                      <h6
                        style={{ color: "#007680", fontWeight: "600" }}
                        className="mb-2"
                      >
                        Personal Information
                      </h6>
                      <Row>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <User size={16} className="me-2 text-muted" />
                            <span>
                              <strong>Gender:</strong>{" "}
                              {paymentLink.metadata.gender}
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <Calendar size={16} className="me-2 text-muted" />
                            <span>
                              <strong>Date of Birth:</strong>{" "}
                              {paymentLink.metadata.dateOfBirth}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <User size={16} className="me-2 text-muted" />
                            <span>
                              <strong>Occupation:</strong>{" "}
                              {paymentLink.metadata.occupation}
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <Shield size={16} className="me-2 text-muted" />
                            <span>
                              <strong>ID Proof:</strong>{" "}
                              {paymentLink.metadata.idProof} -{" "}
                              {paymentLink.metadata.idNumber}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      {/* <Row>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <User size={16} className="me-2 text-muted" />
                            <span>
                              <strong>Height:</strong>{" "}
                              {paymentLink.metadata.height} cm
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="d-flex align-items-center mb-2">
                            <User size={16} className="me-2 text-muted" />
                            <span>
                              <strong>Weight:</strong>{" "}
                              {paymentLink.metadata.weight} kg
                            </span>
                          </div>
                        </Col>
                      </Row> */}
                    </div>

                    {/* Address Information */}
                    {paymentLink.metadata.temporaryAddress && (
                      <div
                        style={{ backgroundColor: "#f8f9fa" }}
                        className="p-3 rounded mb-3"
                      >
                        <h6
                          style={{ color: "#007680", fontWeight: "600" }}
                          className="mb-2"
                        >
                          Temporary Address
                        </h6>
                        <p className="mb-1">
                          {paymentLink.metadata.temporaryAddress.address_line1},{" "}
                          {paymentLink.metadata.temporaryAddress.address_line2}
                        </p>
                        <p className="mb-1">
                          {paymentLink.metadata.temporaryAddress.area},{" "}
                          {paymentLink.metadata.temporaryAddress.city}
                        </p>
                        <p className="mb-1">
                          {paymentLink.metadata.temporaryAddress.state},{" "}
                          {paymentLink.metadata.temporaryAddress.pincode}
                        </p>
                        <p className="mb-0">
                          {paymentLink.metadata.temporaryAddress.country}
                        </p>
                      </div>
                    )}

                    {/* Diseases Information */}
                    {paymentLink.metadata.diseases &&
                      paymentLink.metadata.diseases.length > 0 && (
                        <div
                          style={{ backgroundColor: "#f8f9fa" }}
                          className="p-3 rounded mb-3"
                        >
                          <h6
                            style={{ color: "#007680", fontWeight: "600" }}
                            className="mb-2"
                          >
                            Medical Conditions
                          </h6>
                          {paymentLink.metadata.diseases.map(
                            (disease, index) => (
                              <div key={index} className="mb-2">
                                <strong>{disease.disease_name}</strong>
                                {disease.disease_description && (
                                  <p className="text-muted mb-1 small">
                                    {disease.disease_description}
                                  </p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      )}

                    {/* Other Information */}
                    {/* {paymentLink.metadata.otherInfo && (
                      <div
                        style={{ backgroundColor: "#f8f9fa" }}
                        className="p-3 rounded mb-3"
                      >
                        <h6
                          style={{ color: "#007680", fontWeight: "600" }}
                          className="mb-2"
                        >
                          Additional Information
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <p className="mb-1">
                              <strong>Lead Source:</strong>{" "}
                              {paymentLink.metadata.otherInfo.lead_source}
                            </p>
                            <p className="mb-1">
                              <strong>Campaign:</strong>{" "}
                              {paymentLink.metadata.otherInfo.campaign_name}
                            </p>
                          </div>
                          <div className="col-md-6">
                            <p className="mb-1">
                              <strong>Status:</strong>{" "}
                              {paymentLink.metadata.otherInfo.status}
                            </p>
                            <p className="mb-1">
                              <strong>Priority:</strong>{" "}
                              {paymentLink.metadata.otherInfo.priority}
                            </p>
                          </div>
                        </div>
                      </div>
                    )} */}
                  </div>

                  {/* Payment Information */}
                  {/* <div className="mb-4">
                    <h6
                      style={{ color: "#007680", fontWeight: "600" }}
                      className="mb-3"
                    >
                      <Calendar size={16} className="me-2" />
                      Payment Information
                    </h6>
                    <div className="d-flex align-items-center mb-2">
                      <Calendar size={16} className="me-2 text-muted" />
                      <span>
                        <strong>Created:</strong>{" "}
                        {formatDate(paymentLink.createdAt)}
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <Clock size={16} className="me-2 text-muted" />
                      <span>
                        <strong>Expires:</strong>{" "}
                        {formatDate(paymentLink.expiresAt)}
                      </span>
                    </div>
                  </div> */}

                  {/* Security Notice */}
                  <Alert
                    variant="info"
                    className="mb-4"
                    style={{
                      backgroundColor: "#e7f3ff",
                      borderColor: "#b3d9ff",
                      color: "#0c5460",
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <Lock size={20} className="me-2 mt-1" />
                      <div>
                        <strong>Secure Payment:</strong> Your payment
                        information is encrypted and secure. We use
                        industry-standard SSL encryption to protect your data.
                        This payment is processed by ICICI Bank.
                      </div>
                    </div>
                  </Alert>

                  {/* Payment Button */}
                  {paymentLink.status === "PENDING" && (
                    <div className="text-center">
                      <div className="mb-3">
                        <h5
                          style={{ color: "#007680", fontWeight: "600" }}
                          className="mb-2"
                        >
                          Total Amount to Pay
                        </h5>
                        <h2
                          style={{ color: "#2bc155", fontWeight: "700" }}
                          className="mb-3"
                        >
                          ₹{paymentLink.metadata.amount}
                        </h2>
                      </div>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handlePayment}
                        disabled={processingPayment}
                        className="w-100 py-3"
                        style={{
                          backgroundColor: "#007680",
                          borderColor: "#007680",
                          borderRadius: "8px",
                          fontWeight: "600",
                        }}
                      >
                        {processingPayment ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Processing Payment...
                          </>
                        ) : (
                          <>
                            <CreditCard size={20} className="me-2" />
                            Pay Securely Now
                            <ArrowRight size={16} className="ms-2" />
                          </>
                        )}
                      </Button>
                      <small className="text-muted d-block mt-2">
                        Click to proceed to secure payment gateway
                      </small>
                    </div>
                  )}

                  {paymentLink.status === "PAID" && (
                    <Alert
                      variant="success"
                      className="text-center"
                      style={{
                        backgroundColor: "#d4edda",
                        borderColor: "#c3e6cb",
                        color: "#155724",
                      }}
                    >
                      <CheckCircle size={24} className="me-2" />
                      <strong>Payment Completed!</strong>
                      <br />
                      <small>
                        Transaction ID:{" "}
                        {paymentLink.paymentDetails?.transactionNumber}
                      </small>
                    </Alert>
                  )}

                  {paymentLink.status === "EXPIRED" && (
                    <Alert
                      variant="warning"
                      className="text-center"
                      style={{
                        backgroundColor: "#fff3cd",
                        borderColor: "#ffeaa7",
                        color: "#856404",
                      }}
                    >
                      <Clock size={24} className="me-2" />
                      <strong>Payment Link Expired</strong>
                      <br />
                      Please contact the clinic for a new payment link.
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Instructions */}
              {/* <Card
                className="mt-4 shadow-sm border-0"
                style={{ borderRadius: "12px" }}
              >
                <Card.Body className="p-4">
                  <h6
                    style={{ color: "#007680", fontWeight: "600" }}
                    className="mb-3"
                  >
                    <Info size={16} className="me-2" />
                    How to Complete Your Payment
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div
                          className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "24px",
                            height: "24px",
                            fontSize: "12px",
                            backgroundColor: "#007680",
                          }}
                        >
                          1
                        </div>
                        <div>
                          <strong>Click &quot;Pay Securely Now&quot;</strong>
                          <br />
                          <small className="text-muted">
                            You&apos;ll be redirected to our secure payment
                            gateway
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-start mb-3">
                        <div
                          className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "24px",
                            height: "24px",
                            fontSize: "12px",
                            backgroundColor: "#007680",
                          }}
                        >
                          2
                        </div>
                        <div>
                          <strong>Enter Payment Details</strong>
                          <br />
                          <small className="text-muted">
                            Use your preferred payment method
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div
                          className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "24px",
                            height: "24px",
                            fontSize: "12px",
                            backgroundColor: "#007680",
                          }}
                        >
                          3
                        </div>
                        <div>
                          <strong>Complete Transaction</strong>
                          <br />
                          <small className="text-muted">
                            Confirm your payment details
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-start">
                        <div
                          className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "24px",
                            height: "24px",
                            fontSize: "12px",
                            backgroundColor: "#007680",
                          }}
                        >
                          4
                        </div>
                        <div>
                          <strong>Get Confirmation</strong>
                          <br />
                          <small className="text-muted">
                            Receive payment confirmation and receipt
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card> */}

              {/* Footer */}
              {/* <div className="text-center mt-4">
                <small className="text-muted">
                  © 2025 Rohati Ayurved. All rights reserved.
                </small>
              </div> */}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Payment Processing Modal */}
      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Payment Processing</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <Spinner
            animation="border"
            style={{ color: "#007680" }}
            className="mb-3"
          />
          <h5>Your Payment is Being Processed</h5>
          <p className="text-muted">
            Please complete the payment in the new window that opened. You can
            close this window once payment is completed.
          </p>
          <div style={{ backgroundColor: "#f8f9fa" }} className="p-3 rounded">
            <small className="text-muted">
              <strong>Important:</strong> Do not close the payment window until
              you see the confirmation message.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPaymentModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentLinkPage;
